import React, { useContext, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useState } from 'react';

import { UserContext } from '../context/UserContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUserCircle, 
  faBars, 
  faTimes,
  faChevronDown
} from '@fortawesome/free-solid-svg-icons';

const Navbar = ({ user, onLogout }) => {
    const { currentUser } = useContext(UserContext);
    const { avatar, nombre, role } = currentUser || {};
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isListadosDropdownOpen, setIsListadosDropdownOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    
    // Referencias para detectar clics fuera de los dropdowns
    const dropdownRef = useRef(null);
    const listadosDropdownRef = useRef(null);
    const mobileMenuRef = useRef(null);

    // Alternar estado del menú de perfil
    const toggleDropdown = (e) => {
        e.stopPropagation();
        setIsDropdownOpen(!isDropdownOpen);
        // Cerrar otros dropdowns al abrir este
        if (!isDropdownOpen) {
            setIsListadosDropdownOpen(false);
        }
    };

    // Alternar estado del menú de listados
    const toggleListadosDropdown = (e) => {
        e.stopPropagation();
        setIsListadosDropdownOpen(!isListadosDropdownOpen);
        // Cerrar otros dropdowns al abrir este
        if (!isListadosDropdownOpen) {
            setIsDropdownOpen(false);
        }
    };

    // Alternar estado del menú móvil
    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    // Cerrar menús cuando se hace clic fuera de ellos
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Cerrar dropdown de perfil si se hizo clic afuera
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
            
            // Cerrar dropdown de listados si se hizo clic afuera
            if (listadosDropdownRef.current && !listadosDropdownRef.current.contains(event.target)) {
                setIsListadosDropdownOpen(false);
            }
            
            // No cerrar menú móvil si se hace clic dentro
            if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && 
                event.target.id !== 'mobile-menu-button') {
                setIsMobileMenuOpen(false);
            }
        };

        // Añadir listener de clic
        document.addEventListener('mousedown', handleClickOutside);
        
        // Limpiar al desmontar
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Verificar si el usuario tiene rol "User"
    const isUserRole = role === 'USER';

    return (
        <nav className="relative bg-gradient-to-r from-blue-800 via-blue-900 to-blue-950 shadow-[0_10px_30px_rgba(0,0,0,0.35)] border-b border-white/10">
            {/* Decorative background elements */}
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
            <div className="pointer-events-none absolute top-0 left-1/4 h-32 w-32 rounded-full bg-indigo-400/20 blur-2xl" />
            <div className="pointer-events-none absolute top-0 right-1/4 h-24 w-24 rounded-full bg-cyan-400/20 blur-2xl" />
            
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-16">
                    {/* Logo y enlaces de navegación */}
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <Link to="/" className="text-xl sm:text-2xl font-black tracking-tight text-white drop-shadow-sm">
                                Facturas<span className="text-blue-300">OH</span>
                            </Link>
                        </div>
                        
                        {/* Navegación para pantallas medianas y grandes */}
                        <div className="hidden md:ml-8 md:flex md:space-x-1 md:items-center">
                            {/* Opciones para no-USER */}
                            {!isUserRole && (
                                <>
                                    <Link to="/dashboard" className="text-white/80 hover:text-white hover:bg-white/10 px-4 py-2 text-sm font-medium transition-all duration-200">
                                        Dashboard
                                    </Link>
                                    <Link to="/clientes" className="text-white/80 hover:text-white hover:bg-white/10 px-4 py-2 text-sm font-medium transition-all duration-200">
                                        Clientes
                                    </Link>
                                    <Link to="/facturas" className="text-white/80 hover:text-white hover:bg-white/10 px-4 py-2 text-sm font-medium transition-all duration-200">
                                        Facturas
                                    </Link>
                                    <Link to="/honorarios" className="text-white/80 hover:text-white hover:bg-white/10 px-4 py-2 text-sm font-medium transition-all duration-200">
                                        Honorarios
                                    </Link>
                                </>
                            )}

                            {/* Opción Usuarios solo para ADMIN */}
                            {role === 'ADMIN' && (
                                <Link to="/usuarios" className="text-white/80 hover:text-white hover:bg-white/10 px-4 py-2 text-sm font-medium transition-all duration-200">
                                    Usuarios
                                </Link>
                            )}

                            {/* Opción Vacaciones para ADMIN y FACTURACION */}
                            {(role === 'ADMIN' || role === 'FACTURACION') && (
                                <Link to="/vacaciones" className="text-white/80 hover:text-white hover:bg-white/10 px-4 py-2 text-sm font-medium transition-all duration-200">
                                    Vacaciones
                                </Link>
                            )}

                            {/* Enlaces para USER o dropdown de Listados para otros */}
                            {isUserRole ? (
                                <>
                                    <Link to="/listados" className="text-white/80 hover:text-white hover:bg-white/10 px-4 py-2 text-sm font-medium transition-all duration-200">
                                        Listado Clientes
                                    </Link>
                                    <Link to="/listados/renta" className="text-white/80 hover:text-white hover:bg-white/10 px-4 py-2 text-sm font-medium transition-all duration-200">
                                        Listado Renta
                                    </Link>
                                    <Link to="/estados-renta" className="text-white/80 hover:text-white hover:bg-white/10 px-4 py-2 text-sm font-medium transition-all duration-200">
                                        Estados Renta SII
                                    </Link>
                                    <Link to="/dashboard/rentas" className="text-white/80 hover:text-white hover:bg-white/10 px-4 py-2 text-sm font-medium transition-all duration-200">
                                        Dashboard Rentas
                                    </Link>
                                </>
                            ) : (
                                /* Dropdown de Listados para otros roles */
                                <div className="relative" ref={listadosDropdownRef}>
                                    <button
                                        onClick={toggleListadosDropdown}
                                        className="text-white/80 hover:text-white hover:bg-white/10 px-4 py-2 text-sm font-medium focus:outline-none flex items-center transition-all duration-200"
                                    >
                                        <span>Listados</span>
                                        <FontAwesomeIcon 
                                            icon={faChevronDown} 
                                            className={`ml-1 transition-transform ${isListadosDropdownOpen ? 'transform rotate-180' : ''}`} 
                                            size="xs" 
                                        />
                                    </button>
                                    {isListadosDropdownOpen && (
                                        <div className="absolute left-0 mt-2 w-48 rounded-lg border border-white/20 bg-gray-800/95 backdrop-blur-xl shadow-[0_10px_30px_rgba(0,0,0,0.35)] z-[100] overflow-hidden">
                                            <Link
                                                to="/dashboard/rentas"
                                                className="block px-4 py-3 text-white/90 hover:bg-white/20 hover:text-white transition-all duration-200"
                                                onClick={() => setIsListadosDropdownOpen(false)}
                                            >
                                                Dashboard Rentas
                                            </Link>
                                            <Link
                                                to="/listados"
                                                className="block px-4 py-3 text-white/90 hover:bg-white/20 hover:text-white transition-all duration-200"
                                                onClick={() => setIsListadosDropdownOpen(false)}
                                            >
                                                Listado Clientes
                                            </Link>
                                            <Link
                                                to="/listados/renta"
                                                className="block px-4 py-3 text-white/90 hover:bg-white/20 hover:text-white transition-all duration-200"
                                                onClick={() => setIsListadosDropdownOpen(false)}
                                            >
                                                Listado Renta
                                            </Link>
                                            <Link
                                                to="/estados-renta"
                                                className="block px-4 py-3 text-white/90 hover:bg-white/20 hover:text-white transition-all duration-200"
                                                onClick={() => setIsListadosDropdownOpen(false)}
                                            >
                                                Estados Renta SII
                                            </Link>
                                            <Link
                                                to="/factores-arriendo"
                                                className="block px-4 py-3 text-white/90 hover:bg-white/20 hover:text-white transition-all duration-200"
                                                onClick={() => setIsListadosDropdownOpen(false)}
                                            >
                                                Factores Arriendo
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sección derecha (perfil y botón de menú móvil) */}
                    <div className="flex items-center">
                        {/* Dropdown de perfil */}
                        <div className="relative mr-3" ref={dropdownRef}>
                            <button 
                                onClick={toggleDropdown} 
                                className="flex items-center text-white/80 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-all duration-200 backdrop-blur-sm"
                            >
                                <span className="mr-3 hidden sm:block font-medium">{nombre}</span>
                                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full overflow-hidden ring-2 ring-white/30 shadow-lg">
                                    {avatar ? (
                                        <img 
                                            src={`/assets/avatars/${avatar}`} 
                                            alt="Avatar" 
                                            className="w-full h-full object-cover" 
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
                                            <FontAwesomeIcon icon={faUserCircle} className="text-white text-lg" />
                                        </div>
                                    )}
                                </div>
                            </button>
                            
                            {/* Dropdown de opciones de perfil */}
                            {isDropdownOpen && (
                                <div className="absolute right-0 mt-2 w-48 rounded-lg border border-white/20 bg-gray-800/95 backdrop-blur-xl shadow-[0_10px_30px_rgba(0,0,0,0.35)] z-[100] overflow-hidden">
                                    <Link 
                                        to="/mi-perfil" 
                                        className="block px-4 py-3 text-white/90 hover:bg-white/20 hover:text-white transition-all duration-200"
                                        onClick={() => setIsDropdownOpen(false)}
                                    >
                                        Mi Perfil
                                    </Link>
                                    <button
                                        onClick={() => {
                                            setIsDropdownOpen(false);
                                            onLogout();
                                        }}
                                        className="w-full text-left px-4 py-3 text-white/90 hover:bg-white/20 hover:text-white transition-all duration-200"
                                    >
                                        Cerrar sesión
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Botón para menú móvil */}
                        <button
                            id="mobile-menu-button"
                            onClick={toggleMobileMenu}
                            className="md:hidden inline-flex items-center justify-center p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 focus:outline-none transition-all duration-200 backdrop-blur-sm"
                        >
                            <FontAwesomeIcon icon={isMobileMenuOpen ? faTimes : faBars} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Menú para móviles */}
            <div 
                className={`md:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`}
                ref={mobileMenuRef}
            >
                <div className="px-4 pt-2 pb-3 space-y-1 sm:px-6 border-t border-white/20 bg-black/20 backdrop-blur-md">
                    {/* Enlaces para móvil */}
                    {!isUserRole && (
                        <>
                            <Link 
                                to="/dashboard" 
                                className="block px-4 py-3 rounded-lg text-base font-medium text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Dashboard
                            </Link>
                            <Link 
                                to="/clientes" 
                                className="block px-4 py-3 rounded-lg text-base font-medium text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Clientes
                            </Link>
                            <Link 
                                to="/facturas" 
                                className="block px-4 py-3 rounded-lg text-base font-medium text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Facturas
                            </Link>
                            <Link 
                                to="/honorarios" 
                                className="block px-4 py-3 rounded-lg text-base font-medium text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Honorarios
                            </Link>
                        </>
                    )}

                    {/* Opción Usuarios solo para ADMIN */}
                    {role === 'ADMIN' && (
                        <Link 
                            to="/usuarios" 
                            className="block px-4 py-3 rounded-lg text-base font-medium text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            Usuarios
                        </Link>
                    )}

                    {/* Opción Vacaciones para ADMIN y FACTURACION */}
                    {(role === 'ADMIN' || role === 'FACTURACION') && (
                        <Link 
                            to="/vacaciones" 
                            className="block px-4 py-3 rounded-lg text-base font-medium text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            Vacaciones
                        </Link>
                    )}

                    {/* Enlaces de Listados */}
                    {isUserRole ? (
                        <>
                            <Link 
                                to="/listados" 
                                className="block px-4 py-3 rounded-lg text-base font-medium text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Listado Clientes
                            </Link>
                            <Link 
                                to="/listados/renta" 
                                className="block px-4 py-3 rounded-lg text-base font-medium text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Listado Renta
                            </Link>
                            <Link 
                                to="/estados-renta" 
                                className="block px-4 py-3 rounded-lg text-base font-medium text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Estados Renta SII
                            </Link>
                            <Link 
                                to="/dashboard/rentas" 
                                className="block px-4 py-3 rounded-lg text-base font-medium text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                Dashboard Rentas
                            </Link>
                        </>
                    ) : (
                        <>
                            {/* Enlaces de listados expandidos para móvil */}
                            <div className="border-t border-white/20 my-2 pt-2">
                                <p className="px-4 py-1 text-xs font-semibold text-cyan-300 uppercase tracking-wider">
                                    Listados
                                </p>
                                <Link 
                                    to="/dashboard/rentas" 
                                    className="block px-4 py-3 rounded-lg text-base font-medium text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    Dashboard Rentas
                                </Link>
                                <Link 
                                    to="/listados" 
                                    className="block px-4 py-3 rounded-lg text-base font-medium text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    Listado Clientes
                                </Link>
                                <Link 
                                    to="/listados/renta" 
                                    className="block px-4 py-3 rounded-lg text-base font-medium text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    Listado Renta
                                </Link>
                                <Link 
                                    to="/estados-renta" 
                                    className="block px-4 py-3 rounded-lg text-base font-medium text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    Estados Renta SII
                                </Link>
                                <Link 
                                    to="/factores-arriendo" 
                                    className="block px-4 py-3 rounded-lg text-base font-medium text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                >
                                    Factores Arriendo
                                </Link>
                            </div>
                        </>
                    )}

                    {/* Enlaces de perfil para móvil */}
                    <div className="border-t border-white/20 my-2 pt-2">
                        <Link 
                            to="/mi-perfil" 
                            className="block px-4 py-3 rounded-lg text-base font-medium text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            Mi Perfil
                        </Link>
                        <button
                            onClick={() => {
                                setIsMobileMenuOpen(false);
                                onLogout();
                            }}
                            className="w-full text-left px-4 py-3 rounded-lg text-base font-medium text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
                        >
                            Cerrar sesión
                        </button>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;